import React, { useCallback, useRef, useState } from 'react'
import { useConfirmPassword } from './ConfirmPasswordModal'
import { executeCapability } from '../utils/capabilityExecute'
import {
  SIGNATURE_ACKNOWLEDGEMENT_TEXT,
  SIGNATURE_METADATA_NOTICE,
  type SignatureAuth,
  hasSignatureIdentity,
} from '../utils/signatureAuth'

async function confirmSignatureIdentity(currentPassword: string): Promise<SignatureAuth> {
  return executeCapability<SignatureAuth>({
    app: 'forms',
    capability: 'forms.confirmSignature',
    input: { currentPassword, acknowledged: true },
  })
}

export function useSignatureIdentity(existingValue?: unknown) {
  const { confirmPassword, confirmPasswordModal } = useConfirmPassword()
  const [unlocked, setUnlocked] = useState(() => hasSignatureIdentity(existingValue))
  const authRef = useRef<SignatureAuth | null>(
    hasSignatureIdentity(existingValue)
      ? ((existingValue as { auth: SignatureAuth }).auth)
      : null,
  )

  const requestUnlock = useCallback(async () => {
    const result = await confirmPassword({
      title: 'Confirm your identity to sign',
      description: 'Re-enter your account password to unlock this signature field.',
      confirmLabel: 'Unlock to sign',
      acknowledgement: SIGNATURE_ACKNOWLEDGEMENT_TEXT,
      notice: SIGNATURE_METADATA_NOTICE,
      onConfirm: (currentPassword) => confirmSignatureIdentity(currentPassword),
    })
    if (!result) return null
    authRef.current = result
    setUnlocked(true)
    return result
  }, [confirmPassword])

  const lock = useCallback(() => {
    authRef.current = null
    setUnlocked(false)
  }, [])

  return { unlocked, requestUnlock, lock, authRef, confirmPasswordModal }
}

export const SignaturePadLock: React.FC<{
  unlocked: boolean
  onUnlock: () => void
  children: React.ReactNode
}> = ({ unlocked, onUnlock, children }) => (
  <div className="relative">
    <div className={unlocked ? '' : 'pointer-events-none opacity-40 select-none'}>
      {children}
    </div>
    {!unlocked && (
      <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-base-100/85 p-3">
        <button type="button" className="btn btn-primary btn-sm" onClick={onUnlock}>
          Authenticate to sign
        </button>
      </div>
    )}
  </div>
)
